





















































































































































import type { MergeValues } from '@getaccept/editor-lib';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import ContentBlockContainer from '@getaccept/lib-shared/src/components/ContentBlockContainer.vue';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import CommentCarousel from '@getaccept/lib-shared/src/contextual-commenting/comments-carousel/components/CommentCarousel.vue';
import EditAfterSendNotification from '@getaccept/lib-shared/src/contextual-commenting/components/EditAfterSendNotification.vue';
import FloatingComments from '@getaccept/lib-shared/src/contextual-commenting/components/FloatingComments.vue';
import { FloatingCommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/floating-comments.helper';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import type { CommentParticipant } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-participant';
import type { CommentEditorSelection } from '@getaccept/lib-shared/src/contextual-commenting/types/comment-selection';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-commenting/types/create-reply-input';
import type { CurrentUser } from '@getaccept/lib-shared/src/contextual-commenting/types/current-user';
import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { DocumentType } from '@getaccept/lib-shared/src/enums/document-type';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Entity as EntityShared } from '@getaccept/lib-shared/src/types/entity';
import type { ManuallySignedPage } from '@getaccept/lib-shared/src/types/manually-signed-page';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { ComputedRef, PropType, Ref } from '@vue/composition-api';
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';
import debounce from 'debounce';
import elementResizeDetectorMaker from 'element-resize-detector';
import type { Channel } from '@getaccept/pusher';
import { useScrollToComment } from '@getaccept/lib-shared/src/contextual-commenting/composables/scroll-to-comment.composable';
import DocumentQesFiles from '@getaccept/lib-shared/src/qes/components/DocumentQesFiles.vue';
import type { DocumentQesFile } from '@getaccept/lib-shared/src/qes/types/document-qes-file';
import TrackingService from '../../api/tracking/services/tracking.service';
import DocumentAttachments from '../../documents/components/DocumentAttachments.vue';
import DocumentHeader from '../../documents/components/DocumentHeader.vue';
import router from '../../router';
import { TrackingHelper } from '../../tracking/helpers/tracking.helper';
import { useContextualCommentingData } from '../contextual-commenting/composables/contextual-commenting-data.composable';
import type { PageElement } from '../types/page-element';
import { useContextualCommentingPusher } from '../contextual-commenting/composables/contextual-commenting-pusher.composable';
import { useQesFileDownload } from '../composables/qes-file-download.composable';
import PoweredByButtonMobile from './PoweredByButtonMobile.vue';

export default defineComponent({
  components: {
    DocumentHeader,
    ContentBlockContainer,
    DocumentAttachments,
    PoweredByButtonMobile,
    FloatingComments,
    EditAfterSendNotification,
    CommentCarousel,
    DocumentQesFiles,
  },
  props: {
    document: { type: Object as PropType<SigningPageDocument>, required: true },
    qesFiles: { type: Array as PropType<DocumentQesFile[]>, default: undefined },
    authId: { type: String, default: '' },
    token: { type: Object as PropType<{ jwt: string; exp: number }>, default: undefined },
    editorRecipientInput: { type: Array as PropType<EditorRecipientInput[]>, default: undefined },
    documentPages: { type: Array as PropType<DocumentPage[]>, required: true },
    manuallySignedPages: { type: Array as PropType<ManuallySignedPage[]>, default: undefined },
    pageWidth: { type: Number, default: undefined },
    recipient: { type: Object as PropType<SigningPageRecipient>, default: undefined },
    entity: { type: Object as PropType<Entity>, default: undefined },
    theme: { type: Object as PropType<SigningTheme>, default: undefined },
    videoThumbnail: { type: String, default: undefined },
    videoUrl: { type: String, default: undefined },
    reminderExpirationDays: { type: Number, default: undefined },
    focusedRequiredId: { type: String, default: undefined },
    isPreview: { type: Boolean },
    requiredStepsLeft: { type: Boolean },
    attachments: { type: Array as PropType<DocumentAttachment[]>, default: undefined },
    emptyBottomBar: { type: Boolean },
    initials: { type: String, default: undefined },
    verifiedInitialFieldIds: { type: Array as PropType<string[]>, default: undefined },
    partiallySignedFields: { type: Array as PropType<PartiallySignedEvent[]>, default: undefined },
    trackingDisabled: { type: Boolean },
    recipientPusherChannel: { type: Object as PropType<Channel>, default: undefined },
    showContextualComments: { type: Boolean },
    preventCommentsModification: { type: Boolean },
  },
  setup(props, { emit }) {
    const { execute: downloadQesFile } = useQesFileDownload();

    const pagesContainer = ref<HTMLElement>(null);
    const elementResizeDetector = elementResizeDetectorMaker();
    const dimensionsUpdating = ref(false);
    const { isSmallScreenAndUp: showFloatingComments } = useBreakpoints();

    const contextualCommentingData = useContextualCommentingData();
    const contextualCommentingStore = useContextualCommentingStore();

    const isSigned = computed((): boolean => props.document.status === DocumentStatus.Signed);

    const hasAttachments = computed((): boolean => !!props.attachments.length);

    const allRecipients = computed((): SigningPageRecipient[] => props.document.recipients);

    const currentUser = computed(
      (): CurrentUser => ({
        recipientId: props.recipient.id,
        name: props.recipient.fullName,
        thumbUrl: props.recipient.thumbUrl,
      })
    );

    const hasContent = computed(
      () =>
        !!props.documentPages?.length ||
        !!props.manuallySignedPages?.length ||
        hasAttachments.value ||
        !!props.qesFiles?.length
    );

    const isTemplate = computed(() => props.document.type === DocumentType.Template);

    const allPages: Ref<
      (DocumentPage &
        Partial<{
          visible: boolean;
          element: HTMLElement;
        }>)[]
    > = ref(props.documentPages || []);

    const handleVisibilityChange = ({
      visible,
      pageId,
      element,
    }: {
      visible: boolean;
      pageId: string;
      element: HTMLElement;
    }) => {
      allPages.value = allPages.value.map(page => {
        if (page.id === pageId) {
          return {
            ...page,
            visible,
            element,
          };
        }
        return page;
      });
    };

    const visibleDocumentContentPageElements = computed((): PageElement[] => {
      const filteredTypes: DocumentPageType[] = [
        DocumentPageType.DocumentContent,
        DocumentPageType.ContentLink,
        DocumentPageType.Editor,
      ];

      return allPages.value
        .filter(({ pageType, visible }) => filteredTypes.includes(pageType) && visible)
        .map(({ id, pageType, element }) => ({
          id,
          element,
          type: pageType,
        }));
    });

    const documentManuallySigned = computed(() => !!props.document?.manuallySignedPages?.length);

    const mergeValues = computed(
      (): MergeValues => {
        const {
          value,
          name,
          sendDate,
          uniqueId,
          user,
          recipients,
          expirationDate,
        }: SigningPageDocument = props.document;

        return {
          document: {
            value,
            name,
            sendDate,
            uniqueId,
            expirationDate,
          },
          sender: user,
          recipients,
          entity: props.entity as EntityShared,
        };
      }
    );

    const trackLinkFieldClick = async (id: string) => {
      if (props.trackingDisabled) {
        return;
      }
      try {
        await TrackingService.trackFieldClick(id);
      } catch {
        console.log(`Failed to track click for field with id ${id}`);
      }
    };

    const comments = computed((): Comment[] => contextualCommentingStore.viewableComments);
    const selectedCommentId = computed((): string => contextualCommentingStore.selectedCommentId);

    const { scrollToComment } = useScrollToComment();

    watch(
      () => contextualCommentingStore.selectedCommentId,
      (value: string) => {
        if (!value) {
          return;
        }
        isCommentCarouselOpen.value = true;
        if (!showFloatingComments.value) {
          scrollToComment(value);
        }
      }
    );

    const showComments = computed(
      () =>
        !!floatingComments.value.length &&
        props.showContextualComments &&
        contextualCommentingStore.editorHasRendered
    );

    const pageIds = computed((): string[] =>
      props.documentPages.length ? props.documentPages.map((page: DocumentPage) => page.id) : []
    );

    const floatingComments = computed((): Comment[] => {
      const editorBlocks = FloatingCommentsHelper.getEditorBlocksFromPages(props.documentPages);
      return FloatingCommentsHelper.getComments(comments.value, editorBlocks);
    });

    const filterComments = (_comments: Comment[], pageId: string) =>
      _comments
        .filter((comment: Comment) => !!comment.editorSelection)
        .filter((comment: Comment) => comment.pageId === pageId);

    const handleCreateComment = (editorSelection: CommentEditorSelection, pageId: string) => {
      const currentUser: CommentParticipant = { userId: null, recipientId: props.recipient.id };
      contextualCommentingData.createComment(editorSelection, pageId, currentUser);
    };

    const handleCreateReply = (payload: CreateReplyInput) => {
      const currentUser: CommentParticipant = { userId: null, recipientId: props.recipient.id };
      contextualCommentingData.createReply(payload, currentUser);
    };

    const trackLinkBlockClick = async (id: string) => {
      if (props.trackingDisabled) {
        return;
      }
      try {
        await TrackingService.trackContentLinkBlockClick(id);
      } catch (_) {
        console.log(`Failed to track click for content-link with id ${id}`);
      }
    };

    const distanceToMiddle = (page: PageElement): number => {
      const position: number =
        page.element.offsetHeight / 2 + page.element.getBoundingClientRect().top;
      const middle: number = window.innerHeight / 2;
      return Math.abs(middle - position);
    };

    const calculatePagesInViewPort = (pages: PageElement[]): string[] =>
      pages
        .filter((page: PageElement) => TrackingHelper.isPageInFocus(page))
        .map((page: PageElement) => page.id);

    const calculateCurrentPages = (pages: PageElement[]) => {
      if (!pages.length) {
        return;
      }
      const visiblePages: string[] = calculatePagesInViewPort(pages);
      emit('currentPages', visiblePages);
    };

    const dimensionsChanged = (element: HTMLElement) => {
      emit('updatePageWidth', element.offsetWidth);
      dimensionsUpdating.value = false;
    };

    const debounceEmitDimensionsChanged: (element: HTMLElement) => void = debounce(
      dimensionsChanged,
      300
    );

    const debounceCurrentDocumentPages: (pages: PageElement[]) => void = debounce(
      calculateCurrentPages,
      200
    );

    const contentIds = computed(() =>
      props.documentPages
        .filter((page: DocumentPage) => !!page.editorBlock)
        .map((page: DocumentPage) => page.editorBlock.contentId)
    );

    const { lockedContentIds } = useContextualCommentingPusher(
      props.showContextualComments,
      contentIds
    );

    const createContextualCommentButtonState: ComputedRef<{
      [pageId: string]: CreateContextualCommentButtonState;
    }> = computed(() => {
      if (props.preventCommentsModification) {
        return props.documentPages.reduce((acc, page) => {
          acc[page.id] = CreateContextualCommentButtonState.DocumentIsNotEligible;
          return acc;
        }, {});
      }
      return props.documentPages.reduce((acc, page) => {
        const contentId = page.editorBlock?.contentId;
        if (contentId && lockedContentIds.value.includes(contentId)) {
          acc[page.id] = CreateContextualCommentButtonState.SenderHasMadeChanges;
          return acc;
        }
        acc[page.id] = CreateContextualCommentButtonState.Enabled;
        return acc;
      }, {});
    });

    const isCommentCarouselOpen = ref(false);
    watch(
      () => isCommentCarouselOpen.value,
      () => {
        emit('comments-open', isCommentCarouselOpen.value);
      }
    );
    const handleViewAllComments = () => {
      isCommentCarouselOpen.value = false;
      contextualCommentingStore.toggleCommentHistory(true);
    };

    const handleCloseCommentCarousel = () => {
      isCommentCarouselOpen.value = false;
      contextualCommentingData.selectComment('', props.recipient.id);
    };

    const evaluatePageWidth = (offsetWidth: number) => {
      if (offsetWidth !== props.pageWidth) {
        dimensionsUpdating.value = true;
      }
    };

    let pagesContainerHeight = 0;
    const evaluatePageHeight = (offsetHeight: number) => {
      if (offsetHeight !== pagesContainerHeight) {
        dimensionsUpdating.value = true;
        pagesContainerHeight = offsetHeight;
      }
    };

    onMounted(() => {
      pagesContainerHeight = pagesContainer.value.offsetHeight;
      elementResizeDetector.listenTo(pagesContainer.value, (element: HTMLElement) => {
        evaluatePageWidth(element.offsetWidth);
        evaluatePageHeight(element.offsetHeight);
        debounceEmitDimensionsChanged(element);
        debounceCurrentDocumentPages(visibleDocumentContentPageElements.value);
      });

      window.addEventListener(
        'scroll',
        () => debounceCurrentDocumentPages(visibleDocumentContentPageElements.value),
        { passive: true }
      );
      if (props.showContextualComments) {
        contextualCommentingStore.setCurrentUserId(props.recipient.id);
        contextualCommentingData.loadComments();
      }
    });

    onBeforeUnmount(() => {
      elementResizeDetector.removeListener(pagesContainer.value);
      window.removeEventListener('scroll', () =>
        debounceCurrentDocumentPages(visibleDocumentContentPageElements.value)
      );
    });

    const mentionsEnabled = computed(() => router.currentRoute.query['mentions'] === 'true');

    return {
      elementResizeDetector,
      createContextualCommentButtonState,
      dimensionsUpdating,
      contextualCommentingData,
      contextualCommentingStore,
      isSigned,
      hasAttachments,
      allRecipients,
      currentUser,
      hasContent,
      isTemplate,
      visibleDocumentContentPageElements,
      documentManuallySigned,
      mergeValues,
      trackLinkFieldClick,
      comments,
      selectedCommentId,
      showComments,
      pageIds,
      floatingComments,
      filterComments,
      handleCreateComment,
      handleCreateReply,
      trackLinkBlockClick,
      distanceToMiddle,
      calculatePagesInViewPort,
      calculateCurrentPages,
      dimensionsChanged,
      debounceEmitDimensionsChanged,
      debounceCurrentDocumentPages,
      pagesContainer,
      t,
      showFloatingComments,
      handleViewAllComments,
      handleCloseCommentCarousel,
      isCommentCarouselOpen,
      handleVisibilityChange,
      mentionsEnabled,
      lockedContentIds,
      downloadQesFile,
    };
  },
});
